import { produce } from 'immer';
import { create } from 'zustand';
import { ServiceNameEnum } from '../../../types';
import { DataPeriod } from '../../../utils/enums';
import { DataPeriodState } from './ReportContent/ReportDataPeriod/datePeriod/type';
import { ReportContentItems } from '../../../types/report';

interface GeneralSetting {
  name: string;
  desc: string;
  orgId: string;
  services: ServiceNameEnum[];
}

interface ReportContent {
  template: string;
  period: DataPeriodState;
  sections: ReportContentItems;
}

interface ReportFormat {
  format: string;
}

interface ReportNotification {
  recipients: string[];
  subject: string;
  message: string;
}

interface FormError {
  recipientError: boolean;
}

interface Store {
  // state
  generalSetting: GeneralSetting;
  reportContent: ReportContent;
  reportFormat: ReportFormat;
  notification: ReportNotification;
  error: FormError;

  // setter
  setName: (name: string) => void;
  setDesc: (desc: string) => void;
  setOrgId: (id: string) => void;
  setServices: (s: ServiceNameEnum[]) => void;

  setPeriod: (period: DataPeriodState) => void;
  setSections: (sections: ReportContentItems) => void;

  setRecipients: (recipients: string[]) => void;
  setSubject: (subject: string) => void;
  setMessage: (message: string) => void;

  setRecipientError: (err: boolean) => void;

  // getter
  getGeneralSetting: () => GeneralSetting;
  getReportContent: () => ReportContent;
  getReportFormat: () => ReportFormat;
  getReportNotification: () => ReportNotification;
  getError: () => FormError;

  // reset
  resetCallbackArr: (() => void)[];
  reset: () => void;
  registerResetCallback: (cb: () => void) => void;
  unregisterResetCallback: (cb: () => void) => void;
}

export const useOneTimeReportStore = create<Store>((set, get) => ({
  generalSetting: {
    name: '',
    desc: '',
    orgId: '',
    services: [],
  },

  reportContent: {
    template: 'overall threat detections',
    period: {
      dataPeriod: DataPeriod.LastDay,
      timezoneOffset: '+00:00',
    },
    sections: {
      total: true,
      bec: true,
      phishing: true,
      ransomware: true,
      maliciousFile: true,
      maliciousURL: true,
      spam: true,
      anomaly: true,
      spoofing: true,
      dlp: true,
    },
  },

  reportFormat: {
    format: 'pdf',
  },

  notification: {
    recipients: [],
    subject: '',
    message: '',
  },

  error: {
    recipientError: false,
  },

  resetCallbackArr: [],

  setName: (newName: string) =>
    set(
      produce((state) => {
        state.generalSetting.name = newName;
      })
    ),

  setDesc: (newDesc: string) =>
    set(
      produce((state) => {
        state.generalSetting.desc = newDesc;
      })
    ),

  setOrgId: (id: string) =>
    set(
      produce((state: Store) => {
        state.generalSetting.orgId = id;
      })
    ),

  setServices: (s: ServiceNameEnum[]) =>
    set(
      produce((state: Store) => {
        state.generalSetting.services = s;
      })
    ),

  setPeriod: (period: DataPeriodState) =>
    set(
      produce((state: Store) => {
        state.reportContent.period = period;
      })
    ),

  setSections: (sections: ReportContentItems) =>
    set(
      produce((state: Store) => {
        state.reportContent.sections = sections;
      })
    ),

  setRecipients: (recipients: string[]) =>
    set(
      produce((state: Store) => {
        state.notification.recipients = recipients;
      })
    ),

  setSubject: (s: string) =>
    set(
      produce((state: Store) => {
        state.notification.subject = s;
      })
    ),

  setMessage: (m: string) =>
    set(
      produce((state: Store) => {
        state.notification.message = m;
      })
    ),

  setRecipientError: (err: boolean) =>
    set(
      produce((state: Store) => {
        state.error.recipientError = err;
      })
    ),

  getGeneralSetting: () => get().generalSetting,

  getReportContent: () => get().reportContent,

  getReportFormat: () => get().reportFormat,

  getReportNotification: () => get().notification,

  getError: () => get().error,

  reset: () => get().resetCallbackArr.forEach((cb) => cb()),

  registerResetCallback: (cb) =>
    set(
      produce((state: Store) => {
        state.resetCallbackArr = [...state.resetCallbackArr, cb];
      })
    ),

  unregisterResetCallback: (cb) =>
    set(
      produce((state: Store) => {
        state.resetCallbackArr = state.resetCallbackArr.filter(
          (_cb) => _cb !== cb
        );
      })
    ),
}));
